// ============================================================
// Erinnerungs-Service — manuelle Erinnerungen (Knopf an Terminen/
// Fristen/ToDos). Wird per Sync auf den Server gebracht; der Telegram-
// Bot liefert sie am remind_datum aus und setzt gesendet=true.
//
// DSGVO: Titel/Notiz enthalten NUR administrative Angaben — keine
// Patienten-/Mitarbeiternamen (die sind ohnehin verschlüsselt).
// ============================================================

import { db, type Erinnerung } from '../db/schema';

export type ErinnerungTyp = Erinnerung['typ'];

export interface NeueErinnerung {
  titel: string;
  remind_datum: string;            // ISO-Datum (YYYY-MM-DD)
  typ?: ErinnerungTyp | undefined;
  mandant_id?: string | undefined;
  quelle?: string | undefined;
  notiz?: string | undefined;
}

function heute(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Neue Erinnerung anlegen. Löst über die Sync-Middleware einen Push aus.
 *  Optionale Felder werden nur gesetzt, wenn belegt (exactOptionalPropertyTypes). */
export async function createErinnerung(input: NeueErinnerung): Promise<Erinnerung> {
  const notiz = input.notiz?.trim();
  const e: Erinnerung = {
    id: crypto.randomUUID(),
    schema_version: 1,
    titel: (input.titel || 'Erinnerung').trim().slice(0, 200),
    remind_datum: input.remind_datum,
    typ: input.typ ?? 'FREI',
    erledigt: false,
    gesendet: false,
    erstellt_am: new Date().toISOString(),
    ...(input.mandant_id ? { mandant_id: input.mandant_id } : {}),
    ...(input.quelle ? { quelle: input.quelle } : {}),
    ...(notiz ? { notiz } : {}),
  };
  await db.erinnerungen.add(e);
  return e;
}

/** Alle noch offenen (nicht erledigten) Erinnerungen, nach Datum sortiert. */
export async function loadOffeneErinnerungen(): Promise<Erinnerung[]> {
  const all = await db.erinnerungen.toArray();
  return all
    .filter((e) => !e.erledigt)
    .sort((a, b) => a.remind_datum.localeCompare(b.remind_datum));
}

/** Anstehende Erinnerungen (fällig heute oder überfällig) — für Dashboard-Badge. */
export async function anstehendeErinnerungen(): Promise<Erinnerung[]> {
  const h = heute();
  return (await loadOffeneErinnerungen()).filter((e) => e.remind_datum <= h);
}

/** Erledigt-Status umschalten. */
export async function toggleErinnerungErledigt(id: string): Promise<void> {
  const e = await db.erinnerungen.get(id);
  if (!e) return;
  await db.erinnerungen.put({ ...e, erledigt: !e.erledigt });
}

/** Erinnerung löschen (erzeugt via Middleware einen Sync-Tombstone). */
export async function deleteErinnerung(id: string): Promise<void> {
  await db.erinnerungen.delete(id);
}

/** Ob für eine Quelle bereits eine offene Erinnerung existiert (Dedup/Anzeige). */
export async function hatOffeneErinnerung(quelle: string): Promise<boolean> {
  const all = await db.erinnerungen.toArray();
  return all.some((e) => e.quelle === quelle && !e.erledigt);
}
